import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DatePicker,
  Button,
  notification,
  Select,
  Form,
  Card,
  Row,
  Col,
  Table,
  Tooltip,
  Typography,
  Empty,
} from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import API_SERVICE from "shared/services/api-service";
import type { Moment } from "moment";
import { saveByteArray } from "shared/services/Utility";
import { setScreenHeader } from "shared/redux/common-reducer";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import moment from "moment";
import { RangePickerProps } from "antd/es/date-picker";
import iconError from "../../assets/images/error.svg";
import iconLoaderDot from "../../assets/images/loader-dot.svg";
import iconDownload from "../../assets/images/DownloadQR.svg";
import { ReactComponent as IconDownload } from "../../assets/images/DownloadQR.svg";
import "../../index.css";
import "./reports.scss";
import { requestReportViaWebSocket } from "./report-generate-websocket";

const { RangePicker } = DatePicker;

type Props = {};
type RangeValue = [Moment | null, Moment | null] | null;

type ReportStatus = "IN_PROGRESS" | "COMPLETED" | "ERROR" | "EXPIRED";

type RecentReportRow = {
  id: string;
  reportTypeKey: string;
  fileName: string;
  startDate: string;
  endDate: string;
  status: ReportStatus;
  downloadLink?: string;
  errorMessage?: string;
};

function mapServerStatus(raw: unknown): ReportStatus {
  const u = String(raw ?? "")
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (
    ["IN_PROGRESS", "PENDING", "PROCESSING", "QUEUED", "RUNNING", "SUBMITTED"].includes(
      u
    )
  ) {
    return "IN_PROGRESS";
  }
  if (["COMPLETED", "COMPLETE", "SUCCESS", "DONE", "READY"].includes(u)) {
    return "COMPLETED";
  }
  if (["FAILED", "ERROR", "FAILURE"].includes(u)) {
    return "ERROR";
  }
  if (["EXPIRED"].includes(u)) {
    return "EXPIRED";
  }
  return "IN_PROGRESS";
}

function mapServerItemToRow(item: any, index: number): RecentReportRow {
  const id = String(`row_${index}`);
  const reportTypeKey = item.reportType ;
  const startDate = item.startDate;
  const endDate = item.endDate;
  const fileName = item.fileName ?? "—";
  const downloadLink = typeof item.downloadLink === "string" ? item.downloadLink : undefined;
  const errRaw = item.errorMessage 
  const errorMessage =
    typeof errRaw === "string" && errRaw.trim().length > 0
      ? errRaw.trim()
      : undefined;
  return {
    id,
    reportTypeKey,
    fileName,
    startDate,
    endDate,
    status: mapServerStatus(
      item.status ?? item.requestStatus ?? item.state ?? item.phase
    ),
    downloadLink,
    errorMessage,
  };
}

function resolveReportDownloadHref(record: RecentReportRow): string | undefined {
  return record.downloadLink;
}

/** `userMessage` from a WebSocket JSON frame (root or `payload`), for toast copy. */
function reportWsUserMessage(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  try {
    const o = JSON.parse(trimmed) as Record<string, unknown>;
    const p = o.payload;
    const payloadObj =
      p && typeof p === "object" ? (p as Record<string, unknown>) : undefined;
    const u = o.userMessage ?? payloadObj?.userMessage;
    if (typeof u === "string" && u.trim()) return u.trim();
    return undefined;
  } catch {
    return undefined;
  }
}

/** Supports Spring Page, direct payload, or batch API variants. Ant Design Table omits pagination when `total` is falsy. */
function normalizeReportRequestsResponse(data: any): {
  rows: RecentReportRow[];
  total: number;
} {
  const payload = data?.payload ?? data ?? {};
  const rawList = payload?.content ?? payload?.data ?? payload?.items;
  const list = Array.isArray(rawList) ? rawList : Array.isArray(data) ? data : [];
  const rows = list.map((item: any, i: number) => mapServerItemToRow(item, i));
  const rawTotal =
    payload?.totalElements ??
    payload?.total ??
    payload?.totalCount ??
    payload?.totalRecords ??
    data?.totalElements ??
    data?.total;
  let total = typeof rawTotal === "number" ? rawTotal : Number(rawTotal);
  if (!Number.isFinite(total) || total < 0) {
    total = rows.length;
  }
  return { rows, total };
}

const Reports: React.FunctionComponent<Props> = () => {
  const [loading, setLoading] = useState(false);
  const [dates, setDates] = useState<RangeValue>(null);
  const [value, setValue] = useState<RangeValue>(null);
  const [selectedReportType, setSelectedReportType] = useState(null as any);
  const [reportTypes, setReportTypes] = useState([] as any);
  const [recentRequests, setRecentRequests] = useState<RecentReportRow[]>([]);
  const [tablePage, setTablePage] = useState({ current: 1, pageSize: 10 });
  const [tableTotal, setTableTotal] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  /** Bumps to refetch list (e.g. after new request while already on page 1). */
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [reportForm] = Form.useForm() as any;
  const { appColors }: any = useSelector((state: RootState) => state.common);
  const { tenantInfo }: any = useSelector((state: RootState) => state.tenant);
  const isBroker = localStorage.getItem("USER_TYPE") == "BROKER";
  const blobByIdRef = useRef<Map<string, Blob>>(new Map());

  const dispatch = useDispatch();

  const fetchReportRequests = useCallback(async () => {
    setTableLoading(true);
    try {
      const { data } = await API_SERVICE.getReportRequests(
        tablePage.current,
        tablePage.pageSize
      );
      const { rows, total } = normalizeReportRequestsResponse(data);
      setRecentRequests(rows);
      setTableTotal(total);
    } catch (e: any) {
      notification.error({
        message: API_SERVICE.handleErrors(e),
      });
      setRecentRequests([]);
      setTableTotal(0);
    } finally {
      setTableLoading(false);
    }
  }, [tablePage.current, tablePage.pageSize, tableRefreshKey]);

  useEffect(() => {
    dispatch(setScreenHeader({ screenTitle: "Reports" }));
  }, [dispatch]);

  useEffect(() => {
    fetchReportRequests();
  }, [fetchReportRequests]);

  useEffect(() => {
    getAllTypes();
  }, [tenantInfo]);

  const disabledDate: RangePickerProps["disabledDate"] = (current: Moment) => {
    if (!dates) {
      return current && current > moment().endOf("day");
    }
    if (
      selectedReportType == "USER_CONNECTOR_ACTIVITY" ||
      selectedReportType == "USER_CONNECTOR_CASE_ACTIVITY"
    ) {
      const tooLate = dates[0] && current.diff(dates[0], "days") > 90;
      const tooEarly = dates[1] && dates[1].diff(current, "days") > 90;
      if (dates[0] && moment().diff(dates[0], "days") < 90) {
        return current && current > moment().endOf("day");
      }
      return !!tooEarly || !!tooLate;
    }
    return current && current > moment().endOf("day");
  };

  const getAllTypes = () => {
    API_SERVICE.getTypes()
      .then(({ data }) => {
        let filteredReportTypes = data.find(
          (item: any) => item.propertyName == "REPORT_TYPE"
        );

        if (tenantInfo?.businessProductType === "INSURANCE") {
          const reports = filteredReportTypes.keyAndDisplayValueList?.filter(
            (item: any) => {
              return !(
                item?.key == "BUREAU_AUDIT" ||
                item?.key == "BUREAU_CREDITS_AUDIT" ||
                item?.key == "DISBURSEMENT" ||
                item?.key == "PAYOUT"
              );
            }
          );
          setReportTypes(reports);
        } else {
          const reports = filteredReportTypes.keyAndDisplayValueList?.filter(
            (item: any) => {
              return !["POLICIES", "BROKER_INSURANCE"].includes(item?.key);
            }
          );
          setReportTypes(reports);
        }
      })
      .catch((e: any) => {
        notification.error({ message: API_SERVICE.handleErrors(e) });
      });
  };

  const reportLabelForKey = (key: string) => {
    const found = reportTypes?.find((t: any) => t.key === key);
    return found?.value ?? key;
  };

  const onFinish = (values: any) => {
    let startDate = "";
    let endDate = "";
    const reportType = isBroker ? "BROKER_INSURANCE" : values.reportType;
    const needsRange = "CONNECTOR" !== selectedReportType;
    if (values && values?.dateRange && needsRange) {
      startDate = values.dateRange[0].format("YYYY-MM-DD");
      endDate = values.dateRange[1].format("YYYY-MM-DD");
    }
    requestReportViaWebSocket(
      { startDate, endDate, reportType },
      {
        onMessageReceived: (raw) => {
          const userMessage = reportWsUserMessage(raw);
          if (userMessage) {
            notification.success({
              message: "Report requested",
              description: userMessage,
              placement: "topRight",
              duration: 5,
            });
          }
          void fetchReportRequests();
        },
      }
    )
      .then(() => {
        setLoading(false);
        setTablePage((p) => ({ ...p, current: 1 }));
      })
      .catch((e: unknown) => {
        const message =
          e instanceof Error ? e.message : API_SERVICE.handleErrors(e);
        notification.error({ message });
        setLoading(false);
      });
  };

  const btnPrimaryStyles: any = {
    "--btnColor": appColors?.appPrimaryColor ?? "",
    minWidth: 160,
  };

  const dateRangeLabel = (r: RecentReportRow) => {
    if (!r.startDate || !r.endDate) {
      return "—";
    }
    const a = moment(r.startDate).format("DD/MM/YYYY");
    const b = moment(r.endDate).format("DD/MM/YYYY");
    return `${a} - ${b}`;
  };

  const statusRender = (status: ReportStatus) => {
    switch (status) {
      case "IN_PROGRESS":
        return (
          <span >In Process</span>
        );
      case "COMPLETED":
        return <span >Completed</span>;
      case "ERROR":
        return <span>Error</span>;
      case "EXPIRED":
        return <span>Expired</span>;
      default:
        return status;
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Report Type",
        key: "reportTypeLabel",
        render: (_: unknown, record: RecentReportRow) =>
          reportLabelForKey(record.reportTypeKey) ||
          "—",
      },
      {
        title: "File Name",
        dataIndex: "fileName",
        key: "fileName",
      },
      {
        title: "Date Range",
        key: "range",
        render: (_: unknown, record: RecentReportRow) =>
          dateRangeLabel(record),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (s: ReportStatus) => statusRender(s),
      },
      {
        title: "Actions",
        key: "actions",
        width: 100,
        align: "center" as const,
        render: (_: unknown, record: RecentReportRow) => {
          if (record.status === "IN_PROGRESS") {
            return (
              <Tooltip title="In Progress, Please Wait">
                <img src={iconLoaderDot} width="18px" height="18px" alt="" />
              </Tooltip>
            );
          }
          if (record.status === "ERROR") {
            const errTooltip =
              record.errorMessage || "Please try again";
            return (
              <Tooltip title={errTooltip}>
                <img src={iconError} width="18px" height="18px" alt="" />
              </Tooltip>
            );
          }
          if (record.status === "COMPLETED") {
            const href = resolveReportDownloadHref(record);
            return (
              <Tooltip title="Download">
                <IconDownload
                  className="reports-action-download"
                  onClick={() => {
                    if (href) {
                      window.open(href, "_blank", "noopener,noreferrer");
                      return;
                    }
                    const blob = blobByIdRef.current.get(record.id);
                    if (blob) {
                      saveByteArray([blob], record.fileName);
                    }
                  }}
                />
              </Tooltip>
            );
          }
          return null;
        },
      },
    ],
    [reportTypes]
  );

  const handleTableChange = (pagination: any) => {
    setTablePage({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  const reportTableStyles: any = appColors?.appPrimaryColor
    ? { "--bgColor": appColors.appPrimaryColor }
    : undefined;

  return (
    <div className="reports-screen">
      <Card className="custom-app-card content-box reports-request-card">
        <Form
          layout="vertical"
          form={reportForm}
          initialValues={{}}
          onFinish={onFinish}
          autoComplete="off"
          name="reportForm"
        >
          <Row gutter={[24, 16]} align="bottom">
            {!isBroker && (
              <Col xs={24} sm={24} md={8} lg={7}>
                <Form.Item
                  label="Select Report Type"
                  rules={[
                    { required: true, message: "Please select report type" },
                  ]}
                  required
                  name="reportType"
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Select Report Type"
                    style={{ width: "100%" }}
                    onChange={(e) => {
                      setSelectedReportType(e);
                      reportForm.setFieldsValue({
                        dateRange: null,
                      });
                    }}
                  >
                    {reportTypes &&
                      reportTypes
                        .sort(function (a: any, b: any) {
                          if (a.key.toLowerCase() < b.key.toLowerCase())
                            return -1;
                          if (a.key.toLowerCase() > b.key.toLowerCase())
                            return 1;
                          return 0;
                        })
                        .map((option: any, i: number) => (
                          <Select.Option key={i} value={option.key}>
                            {option.value}
                          </Select.Option>
                        ))}
                  </Select>
                </Form.Item>
              </Col>
            )}

            {"CONNECTOR" !== selectedReportType ? (
              <Col xs={24} sm={24} md={isBroker ? 14 : 10} lg={isBroker ? 12 : 10}>
                <Form.Item
                  label="Select Date Range"
                  rules={[
                    {
                      required: "CONNECTOR" !== selectedReportType,
                      message: "Please select date range",
                    },
                  ]}
                  required={"CONNECTOR" !== selectedReportType}
                  name="dateRange"
                  style={{ marginBottom: 0 }}
                >
                  <RangePicker
                    value={dates || value}
                    disabledDate={disabledDate}
                    onCalendarChange={(val) => setDates(val)}
                    onChange={(val) => setValue(val)}
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder={["Start Date", "End Date"]}
                  />
                </Form.Item>
              </Col>
            ) : null}

            <Col
              xs={24}
              sm={24}
              md={isBroker ? 10 : 6}
              lg={isBroker ? 8 : 7}
              className="reports-request-actions"
            >
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  size="large"
                  loading={loading}
                  type="primary"
                  htmlType="submit"
                  style={btnPrimaryStyles}
                  className="dynamic-btn-primary"
                >
                  Request Report
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card className="custom-app-card content-box reports-recent-card">
        <div className="reports-recent-header">
          <Typography.Title level={5} className="reports-recent-title">
            Recent Requests
          </Typography.Title>
          <Tooltip title="Refresh">
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={() => setTableRefreshKey((k) => k + 1)}
              className="reports-refresh-btn"
              aria-label="Refresh recent requests"
            />
          </Tooltip>
        </div>
        <Row>
          <Col span={24}>
            <Table<RecentReportRow>
              rowKey="id"
              className="dynamic-pagination-checkbox"
              style={reportTableStyles}
              columns={columns as any}
              dataSource={recentRequests}
              loading={tableLoading}
              scroll={{ x: "max-content" }}
              size="small"
              onChange={handleTableChange}
              pagination={{
                current: tablePage.current,
                pageSize: tablePage.pageSize,
                total: tableTotal,
                showSizeChanger: true,
              }}
              locale={{
                emptyText: (
                  <Empty
                    className="reports-empty"
                    description="No Data"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default Reports;
